/**
 * App-foreground time tracker.
 *
 * Counts seconds while the page is visible (not minimised / tab-hidden) and
 * the user has interacted at least once (browsers won't fire visibility events
 * for a tab that was opened but never focused). Periodically POSTs the delta
 * to /api/staff/app-session-ping and resets.
 *
 * Public API:
 *   startAppSessionTracker()   — call once after login
 *   stopAppSessionTracker()    — call on logout / unmount
 */
const FLUSH_INTERVAL_MS = 60_000;       // flush every 60 s
const FLUSH_JITTER_RATIO = 0.25;        // ±25 % — de-sync the fleet's pings
const TICK_INTERVAL_MS = 1_000;         // accumulate every second
const API_URL = process.env.REACT_APP_BACKEND_URL;

let _state = null;

function _isActive() {
  return typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus();
}

// v2.4.9 (Feb 26, 2026) — jittered self-rescheduling flush timer so the
// ~800-device fleet doesn't POST /api/staff/app-session-ping in a
// synchronized 60 s burst (see morning-window RCA).
function _scheduleFlush() {
  const spread = FLUSH_INTERVAL_MS * FLUSH_JITTER_RATIO;
  const next = Math.max(15_000, FLUSH_INTERVAL_MS + (Math.random() * 2 - 1) * spread);
  return setTimeout(async () => {
    await _flush();
    if (_state) _state.flushId = _scheduleFlush();
  }, next);
}

async function _flush() {
  if (!_state || _state.pendingSeconds <= 0) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  const delta = Math.min(_state.pendingSeconds, 3600);
  try {
    const r = await fetch(`${API_URL}/api/staff/app-session-ping`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ foreground_seconds: delta }),
    });
    if (r.ok) _state.pendingSeconds -= delta;
  } catch {}
}

export function startAppSessionTracker() {
  if (_state) return; // already running
  _state = {
    pendingSeconds: 0,
    tickId: setInterval(() => {
      if (_isActive()) _state.pendingSeconds += TICK_INTERVAL_MS / 1000;
    }, TICK_INTERVAL_MS),
    flushId: _scheduleFlush(),
    onVis: () => { if (!_isActive()) _flush(); },
    onUnload: () => {
      // Best-effort: use sendBeacon so the request survives page unload.
      const token = localStorage.getItem('token');
      if (!_state || !token || _state.pendingSeconds <= 0) return;
      try {
        const blob = new Blob(
          [JSON.stringify({ foreground_seconds: Math.min(_state.pendingSeconds, 3600) })],
          { type: 'application/json' }
        );
        // sendBeacon doesn't support custom headers — fall back to keepalive fetch
        // for the auth header to come through.
        fetch(`${API_URL}/api/staff/app-session-ping`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: blob,
        }).catch(() => {});
      } catch {}
    },
  };
  document.addEventListener('visibilitychange', _state.onVis);
  window.addEventListener('beforeunload', _state.onUnload);
}

export function stopAppSessionTracker() {
  if (!_state) return;
  _flush();
  clearInterval(_state.tickId);
  clearTimeout(_state.flushId);
  document.removeEventListener('visibilitychange', _state.onVis);
  window.removeEventListener('beforeunload', _state.onUnload);
  _state = null;
}
